import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';

import { SettingsStoreDispatcher } from '@common/dispatchers/settings.dispatcher';
import { ModalStoreDispatcher } from '@common/dispatchers/modal.dispatcher';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ModalsService } from '@core/services/modals/modals.service';

import { Cb2PreviewStateService } from './cb2-preview-state.service';
import { Cb2PreviewBootService } from './cb2-preview-boot.service';
import { Cb2PreviewMarketplaceApplierService } from './cb2-preview-marketplace-applier.service';
import { Cb2PreviewPickGuardService } from './cb2-preview-pick-guard.service';
import { Cb2BlocHighlightService } from './cb2-bloc-highlight.service';
import {
  CB2_PREVIEW_MESSAGE_SOURCE_OUT,
  Cb2PreviewInboundMessage,
  Cb2PreviewOutboundMessage,
  Cb2PreviewRuntimePayload,
  isCb2PreviewInboundMessage,
} from './cb2-preview-protocol';
import { take } from 'rxjs/operators';

/**
 * Receives live preview commands from the CB2 Studio dashboard (parent window)
 * and delegates them to the preview applier, which projects the payload into the
 * existing NgRx stores + UI + routes so the unchanged rendering pipeline
 * (BlockFactory → ComponentFactory) re-renders.
 *
 * The marketplace stub boot is owned by `Cb2PreviewBootService` +
 * `MarketplaceService.loadMarketplace$`; this bridge only handles ongoing
 * refresh/locale/viewport/pick commands and signals `preview-ready` once the
 * initial stub has been applied.
 *
 * No CB2 runtime is added to production: the bridge is only armed when
 * `Cb2PreviewStateService.isActive()` is true (set by the
 * `activateCb2PreviewMode` APP_INITIALIZER on `?cbPreview=1`).
 */
@Injectable({ providedIn: 'root' })
export class PreviewBridgeService implements OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly settingsDispatcher = inject(SettingsStoreDispatcher);
  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly modalsService = inject(ModalsService);
  private readonly modalSelector = inject(ModalStoreSelector);
  private readonly modalDispatcher = inject(ModalStoreDispatcher);
  private readonly cb2PreviewBoot = inject(Cb2PreviewBootService);
  private readonly cb2PreviewApplier = inject(Cb2PreviewMarketplaceApplierService);
  private readonly pickGuard = inject(Cb2PreviewPickGuardService);
  private readonly blocHighlight = inject(Cb2BlocHighlightService);

  private messageListener: ((event: MessageEvent) => void) | null = null;

  ngOnDestroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    this.pickGuard.sync(false);
  }

  /** Arm the postMessage listener. No-op outside preview mode. */
  init(): void {
    if (!this.previewState.isActive()) return;
    if (this.messageListener) return;

    this.messageListener = (event: MessageEvent) => this.onMessage(event);
    window.addEventListener('message', this.messageListener);

    // Signal readiness only after the initial marketplace stub has been
    // applied (the boot service replays it to every subscriber).
    this.cb2PreviewBoot
      .waitForInitialPayload()
      .pipe(take(1))
      .subscribe(() => this.signalPreviewReady());
  }

  private onMessage(event: MessageEvent): void {
    if (!isCb2PreviewInboundMessage(event.data)) return;

    // Run inside Angular zone so signal/store updates trigger CD.
    this.ngZone.run(() => this.handleInbound(event.data));
  }

  private handleInbound(message: Cb2PreviewInboundMessage): void {
    switch (message.type) {
      case 'refresh-preview':
        this.previewState.setMarketplaceId(message.marketplaceId ?? null);
        this.previewState.setPageKey(message.pageKey ?? null);
        this.previewState.setLocale(message.lang ?? null);
        if (message.lang) this.settingsDispatcher.updateAppLocale(message.lang);
        // Device must be applied before `$cases` resolution — same contract as `lang`.
        if (message.device) this.previewState.setDevice(message.device);
        this.applyRuntimePayload(message.payload);
        break;

      case 'set-preview-locale':
        this.previewState.setLocale(message.lang ?? null);
        if (message.lang) this.settingsDispatcher.updateAppLocale(message.lang);
        if (message.device) this.previewState.setDevice(message.device);
        this.applyRuntimePayload(message.payload);
        break;

      case 'set-preview-viewport':
        this.previewState.setViewport(message.preset, message.width, message.height);
        // Prefer an explicit payload (parity with locale); fall back to last draft.
        if (message.payload) {
          this.applyRuntimePayload(message.payload);
        } else {
          this.cb2PreviewApplier.reapplyLastPayload();
        }
        break;

      case 'set-preview-editor-pick':
        this.previewState.setPickEnabled(message.enabled);
        // Capture-phase guard: block widget side effects while picking.
        this.pickGuard.sync(message.enabled);
        break;

      case 'scroll-to-bloc':
        this.scrollToBloc(message.blocKey);
        break;

      case 'cb2:open-modal':
        this.openPreviewModal(message.handle);
        break;

      case 'cb2:close-modals':
        this.modalDispatcher.reset();
        break;
    }
  }

  /** Forwards a runtime payload to the applier (idempotent / deduped). */
  private applyRuntimePayload(payload?: Cb2PreviewRuntimePayload): void {
    if (!payload) return;
    this.cb2PreviewApplier.apply(payload);
    this.syncOpenModalShellData(payload);
  }

  private openPreviewModal(handle: string): void {
    if (!handle) return;

    const isAlreadyOpen = this.modalSelector.modals.some(modal => modal.handle === handle);
    if (isAlreadyOpen) {
      return;
    }

    this.modalsService.open(handle as never);
  }

  private syncOpenModalShellData(payload: Cb2PreviewRuntimePayload): void {
    const openModals = this.modalSelector.modals;
    if (openModals.length === 0) {
      return;
    }

    const payloadModals = (payload.modals ?? []) as Array<{
      handle?: string;
      data?: Record<string, unknown>;
    }>;

    for (const openModal of openModals) {
      const entry = payloadModals.find(modal => modal.handle === openModal.handle);
      if (!entry?.data) {
        continue;
      }

      this.modalsService.setData(openModal.handle as never, entry.data as never);
    }
  }

  private readAuthProviderSnapshot(
    payload: Cb2PreviewRuntimePayload,
  ): Record<string, unknown> | null {
    const modals = (payload.modals ?? []) as Array<{
      handle?: string;
      data?: Record<string, unknown>;
    }>;
    const authModal = modals.find(modal => modal.handle === 'authentication-modal');
    if (!authModal?.data) {
      return null;
    }

    const data = authModal.data;
    return {
      google: data['authenticationProviderGoogle'] ?? data['authentication_provider_google'],
      facebook: data['authenticationProviderFacebook'] ?? data['authentication_provider_facebook'],
      apple: data['authenticationProviderApple'] ?? data['authentication_provider_apple'],
    };
  }

  /**
   * Tells the dashboard the iframe is ready to receive the inline spec.
   * Re-sent whenever the dashboard asks again (idempotent).
   */
  signalPreviewReady(): void {
    if (!this.previewState.isActive()) return;
    const parent = window.parent;
    if (!parent) return;

    const message: Cb2PreviewOutboundMessage = {
      source: CB2_PREVIEW_MESSAGE_SOURCE_OUT,
      type: 'cb2:preview-ready',
      marketplaceId: this.marketplaceSelector.selectMarketplaceId(),
      pageKey: this.previewState.pageKey() ?? undefined,
    };
    parent.postMessage(message, '*');
  }

  /**
   * Flashes a highlight outline on the block matching `blocKey` without scrolling
   * the preview. Centering via scrollIntoView was shifting kiosk pages (footer
   * selection created white space below). Targets the unique
   * `[data-cb2-bloc-key]` attribute so several instances of the same block type
   * are addressed independently.
   *
   * Runs outside Angular: pure DOM, no store/signal update. Retries once after a
   * microtask batch in case the block is still being rendered (CD / route change).
   */
  private scrollToBloc(blocKey: string): void {
    if (!this.previewState.isActive() || !blocKey) return;

    this.ngZone.runOutsideAngular(() => {
      const target = this.findBlocElement(blocKey);
      if (target) {
        this.revealBloc(target);
        return;
      }
      // Block not in the DOM yet — retry once after the current change detection
      // / route projection flushes.
      setTimeout(() => {
        const deferred = this.findBlocElement(blocKey);
        if (deferred) this.revealBloc(deferred);
      }, CB2_SCROLL_TO_BLOC_RETRY_MS);
    });
  }

  private findBlocElement(blocKey: string): HTMLElement | null {
    const selector = `[data-cb2-bloc-key="${CSS.escape(blocKey)}"]`;
    return document.querySelector<HTMLElement>(selector);
  }

  private revealBloc(target: HTMLElement): void {
    // The wrapper is `display: contents` (no box) and must stay that way to
    // keep the preview layout identical to production, so the flash outline is
    // painted as a fixed overlay instead of promoting the wrapper to a block.
    this.blocHighlight.flash(target);
  }
}

const CB2_SCROLL_TO_BLOC_RETRY_MS = 120;
